import { useEffect } from 'react';

import {
	useAvailableSitesState,
	useMappingsActions,
	useIssueTypesState,
	useProjectsState,
} from '../providers/BulkConfigureContextProvider.tsx';
import { useIssueCreateSidePanelContext } from '../providers/IssueCreateSidePanelContextProvider.tsx';
import { getJiraWorkItemKeysFromTable } from '../utils/tableUtils.tsx';
import { SimpleJiraIssuesSet } from '../__types__/apiUtils.ts';

import { executeGraphQLQuery, ParentCandidatesForNewIssueQuery } from './apiUtils.ts';

const BATCH_SIZE = 20;
const ParentCandidatesForNewIssueQueryOperationName = 'ParentCandidatesForNewIssueQuery';

export const useGetJiraParentCandidates = (): void => {
	const [{ selectedTable }] = useIssueCreateSidePanelContext();
	const { selectedAvailableSite } = useAvailableSitesState();
	const { selectedProject } = useProjectsState();
	const { selectedIssueType } = useIssueTypesState();
	const { setMappingValidParentCandidates } = useMappingsActions();

	useEffect(() => {
		const fetchParentCandidates = async () => {
			const cloudId = selectedAvailableSite?.cloudId;
			const projectId = selectedProject?.id;
			const issueTypeId = selectedIssueType?.id;

			if (!selectedTable || !cloudId || !issueTypeId || !projectId) {
				// Clear the parent candidates if we don't have the required data
				setMappingValidParentCandidates(new SimpleJiraIssuesSet());
				return;
			}

			const allWorkItemKeysFlat = Array.from(
				getJiraWorkItemKeysFromTable(selectedTable).values(),
			).flat();

			if (allWorkItemKeysFlat.length === 0) {
				setMappingValidParentCandidates(new SimpleJiraIssuesSet());
				return;
			}

			const batches: string[][] = [];
			for (let i = 0; i < allWorkItemKeysFlat.length; i += BATCH_SIZE) {
				batches.push(allWorkItemKeysFlat.slice(i, i + BATCH_SIZE));
			}

			const results = new SimpleJiraIssuesSet();

			for (const batch of batches) {
				const batchPromises = batch.map(async (issueKey) => {
					// Gets the parent candidates of the given issue type and checks to see if the issue key is a parent candidate.
					const response = await executeGraphQLQuery(
						{
							query: ParentCandidatesForNewIssueQuery,
							variables: {
								projectId,
								cloudId,
								issueTypeId,
								searchTerm: issueKey,
								excludeDone: true,
								first: 1,
							},
							operationName: ParentCandidatesForNewIssueQueryOperationName,
						},
						cloudId,
					);
					// The GraphQL response structure:
					// response.data.jira.parentCandidatesWithInfoForNewIssue.parentCandidates.edges
					const edges =
						response?.data?.jira?.parentCandidatesWithInfoForNewIssue?.parentCandidates?.edges;

					if (Array.isArray(edges)) {
						const issueNode = edges.find(
							(edge) => edge && edge.node && edge.node.key === issueKey,
						)?.node;
						if (issueNode) {
							return {
								issueId: issueNode.issueId,
								key: issueNode.key,
							};
						}
					}

					return null;
				});
				const batchResults = await Promise.all(batchPromises);
				batchResults.forEach((issue) => {
					if (issue) {
						results.add(issue);
					}
				});
			}

			setMappingValidParentCandidates(results);
		};

		void fetchParentCandidates();
	}, [
		selectedTable,
		selectedAvailableSite?.cloudId,
		selectedIssueType?.id,
		setMappingValidParentCandidates,
		selectedProject?.id,
	]);
};
